import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldValues, Path } from 'react-hook-form';
import { Control, FieldPath } from 'react-hook-form/dist/types';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';
import {
  convertPropertyAccessorToFunction,
  StyledAutocomplete,
} from './StyledAutocomplete';
import {
  DropDownInputProps,
  StyledAutocompleteControl,
  StyledAutocompleteProps,
} from './types';

export type HookFormProps<
  T,
  Required extends boolean | undefined = undefined,
  UseIdAsValue extends boolean | undefined = undefined,
  TFieldValues extends FieldValues = FieldValues,
> = Omit<
  DropDownInputProps<T, Required, UseIdAsValue>,
  'onChange' | 'onValueChanged' | 'value'
> & {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: Exclude<RegisterOptions, 'valueAsDate' | 'setValueAs'>;
  onFocus?: () => void;
  defaultValue?: unknown;
};

export function HookFormDropDownInput<
  T,
  Required extends boolean | undefined = undefined,
  UseIdAsValue extends boolean | undefined = undefined,
  TFieldValues extends FieldValues = FieldValues,
>(props: HookFormProps<T, Required, UseIdAsValue, TFieldValues>) {
  const { useIdFunctionAsValue = true, control, name, rules, ...rest } = props;
  const idFunction = props.idFunction
    ? convertPropertyAccessorToFunction<T, false, Required, false>(
        props.idFunction,
      )
    : undefined;
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, onBlur, value } }) => {
        return (
          <DropDownInput<T, Required, true>
            useIdFunctionAsValue={true}
            {...rest}
            value={value}
            onBlur={onBlur}
            variant={props.variant ?? 'formInput'}
            onValueChanged={(v: T | null) => {
              if (useIdFunctionAsValue && idFunction && v) {
                onChange(idFunction(v));
              } else {
                onChange(v);
              }
            }}
          />
        );
      }}
    />
  );
}

export function DropDownInput<
  T,
  Required extends boolean | undefined = undefined,
  UseIdAsValue extends boolean | undefined = undefined,
>(props: DropDownInputProps<T, Required, UseIdAsValue>) {
  const { onValueChanged, useIdFunctionAsValue, ...rest } = props;
  const onValueChanged_ValueRef = useRef(props.value);

  const idFunction = props.idFunction
    ? convertPropertyAccessorToFunction<T, false, Required, false>(
        props.idFunction,
      )
    : (v: any) => v;
  let value = props.value;
  if (
    props.useIdFunctionAsValue &&
    !!idFunction &&
    // value === 0 could be a valid value here
    value !== undefined &&
    value !== null
  ) {
    value = props.options.find((x) => idFunction(x as any) == value) as any;
  }
  const onChange: StyledAutocompleteProps<
    T,
    false,
    Required,
    false
  >['onChange'] = useMemo(
    () => (e, item) => {
      const result = onValueChanged(item as any);
      if (!props.disableAutomaticResetAfterOnValueChanged) {
        Promise.resolve(result)
          .then(() => {
            onValueChanged_ValueRef.current = item as any;
            setResetValueIfUnchangedAfterCallingOnValueChanged(
              new Date().getTime(),
            );
          })
          .catch((ex: any) => console.error(ex));
      }
    },
    [onValueChanged],
  );

  const [
    resetValueIfUnchangedAfterCallingOnValueChanged,
    setResetValueIfUnchangedAfterCallingOnValueChanged,
  ] = useState(0);
  useEffect(
    function resetValueIfUnchangedAfterCallingOnValueChanged() {
      /*
       * Use case: user selected something in DropDown, we've shown a confirmation like 'Are you sure you want to change the value?' and user said No.
       * In this case we need to revert the value in DropDown.
       * But technically `value` in the state wasn't yet changed, so there's nothing to revert.
       *
       * To overcome this, we verify that if `props.value` wasn't changed after calling `onValueChanged`, we reset the value in DropDown.
       */
      if (props.value != idFunction(onValueChanged_ValueRef.current as any)) {
        actionsRef.current?.blur();
      }
    },
    [resetValueIfUnchangedAfterCallingOnValueChanged],
  );

  const actionsRef = useRef<StyledAutocompleteControl>(null);
  return (
    <StyledAutocomplete<T, false, Required, false>
      {...rest}
      multiple={false}
      value={value as any}
      onChange={onChange}
      actions={actionsRef}
    />
  );
}
